import { Formik } from 'formik';
import { useCallback, useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useManagedIntegrationList } from '@frontend/api';
import { useToast } from '@frontend/components';

import { featureFlagDrawer } from 'modules/common/drawers/featureFlagDrawer/data/actions';
import {
  useFeatureFlagsQuery,
  useUpdateFeatureFlagsMutation,
} from 'modules/common/drawers/featureFlagDrawer/data/hooks';
import { getFeatureFlagFormValidationSchema } from 'modules/common/drawers/featureFlagDrawer/utils';

import { fetchProfile as fetchProfileActionCreators } from 'store/session/actions';
import { getGroupId } from 'store/session/selectors';

import { FeatureFlagFormContextProvider } from './FeatureFlagFormContext';

export type FormWrapperProps = PropsWithChildren<unknown>;

export const FormWrapper = ({ children }: FormWrapperProps) => {
  const dispatch = useDispatch();

  const closeDrawer = useCallback(
    () => dispatch(featureFlagDrawer.close()),
    [dispatch],
  );

  const fetchProfile = () => dispatch(fetchProfileActionCreators.request());

  const toast = useToast();

  const groupLocator = useSelector(getGroupId);

  const featureFlagsQueryResult = useFeatureFlagsQuery(groupLocator);

  const updateFeatureFlagsMutation = useUpdateFeatureFlagsMutation(
    groupLocator,
    fetchProfile,
  );
  const { isLoading: isSubmitting } = updateFeatureFlagsMutation;

  const managedIntegrationListQueryResult = useManagedIntegrationList();
  const { isError: managedIntegrationListQueryIsError } =
    managedIntegrationListQueryResult;

  useEffect(() => {
    if (managedIntegrationListQueryIsError) {
      toast({
        title: 'Managed Integrations',
        description:
          'Failed to retrieve the number of integrations, defaulting to 0',
        status: 'warning',
      });
    }
  }, [managedIntegrationListQueryIsError, toast]);

  useEffect(() => {
    if (updateFeatureFlagsMutation.isSuccess) {
      closeDrawer();
    }
  }, [closeDrawer, updateFeatureFlagsMutation.isSuccess]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={featureFlagsQueryResult.data}
      validateOnChange={false}
      validationSchema={getFeatureFlagFormValidationSchema(
        featureFlagsQueryResult.data,
        managedIntegrationListQueryResult.data,
      )}
      onSubmit={(featureFlags) => {
        updateFeatureFlagsMutation.mutate(featureFlags);
      }}
    >
      {({ handleSubmit }) => (
        <FeatureFlagFormContextProvider
          value={{
            featureFlagsQueryResult,
            isSubmitting,
            handleSubmit,
          }}
        >
          {children}
        </FeatureFlagFormContextProvider>
      )}
    </Formik>
  );
};
